import {
    AfterViewChecked,
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    OnDestroy,
    ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, shareReplay, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { isNotNullOrUndefined, TableColumnConfig } from 'src/app/helper/helper';
import { MatSort } from '@angular/material/sort';
import {
    getContractGroupType,
    getContracts,
    getContractStatus,
} from '../../../../dave-data-module/State/selectors/mdqx/selectors';
import { MatTableDataSource } from '@angular/material/table';
import { State } from '../../../../dave-data-module/State';
import {
    PermissionService
} from '../../../../dave-utils-module/dave-shared-components-module/services/permission.service';


/**
 * Form der ContractList-Tabellendaten.
 * Besteht aus Teilen von Contract, ContractGroupType und ContractStatus.
 */
interface TableData {
    Abrechnungszyklus: string;
    ContractGroupTypeName: string;
    ContractStatusName: string;
    Description: string;
    Name: string;
    Number: string;
    RouterLink: string;
}

@Component({
    selector: 'app-contract-list',
    templateUrl: './contract-list.component.html',
    styleUrls: ['./contract-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContractListComponent implements OnDestroy, AfterViewInit, AfterViewChecked {
    /** FormControl des Vertragssuchfeldes */
    public InputForm: FormControlTyped<string> = new FormControl();

    /** Ob das Suchfeld angezeigt wird */
    public IsSearching$ = new BehaviorSubject(false);

    /** Ob die Komponente unter einer bestimmten Breite ist */
    public IsSmallWidth$ = new BehaviorSubject(false);

    /** Konfiguration der Spalten der MatTable */
    public Columns: TableColumnConfig<TableData>[];

    /** Angezeigte Spalten der MatTable */
    public DisplayedColumns$: Observable<string[]>;

    /** Daten der Tabelle */
    public DataSource$: Observable<MatTableDataSource<TableData>>;

    /** Das Suchfeld-Element */
    @ViewChild('input') private inputRef?: ElementRef<HTMLInputElement>;

    /** Der `MatSort` der Tabelle */
    @ViewChild(MatSort) private matSort?: MatSort;

    /** Gibt ein Mal einen Wert aus, wenn der ngAfterViewInit Hook ausgelöst wird */
    private afterViewInit$ = new Subject<void>();

    /** Gibt ein Mal einen Wert aus, wenn die Komponente zerstört wird */
    private onDestroy$ = new Subject<void>();

    constructor(private elementRef: ElementRef<HTMLElement>, private store: Store<State>, public PS: PermissionService,) {
        this.Columns = [
            { header: 'Abrechnungszyklus', name: 'Abrechnungszyklus' },
            { header: 'V-Gruppe', name: 'ContractGroupTypeName' },
            { header: 'Status', name: 'ContractStatusName' },
            { header: 'Beschreibung', name: 'Description' },
            { header: 'Name', name: 'Name' },
            { header: 'V-Nummer', name: 'Number' },
        ];

        this.DisplayedColumns$ = this.IsSmallWidth$.pipe(
            map(isTableSmallWidth =>
                isTableSmallWidth
                    ? ['Number', 'Name', 'chevron']
                    : [
                          'Number',
                          'Name',
                          'ContractGroupTypeName',
                          'Description',
                          'Abrechnungszyklus',
                          'ContractStatusName',
                          'chevron',
                      ],
            ),
            shareReplay(1), // wird mehrmals subscribed
        );

        this.DataSource$ = combineLatest([
            this.store.select(getContracts).pipe(filter(isNotNullOrUndefined)),
            this.store.select(getContractGroupType).pipe(filter(isNotNullOrUndefined)),
            this.store.select(getContractStatus).pipe(filter(isNotNullOrUndefined)),
            this.PS.Has$(this.PS.Permissions.GetCustomer),
            this.afterViewInit$, // gibt erst etwas aus, nachdem Angular this.matSort gesetzt hat
        ]).pipe(
            map(([contracts, contractGroupTypes, contractStatuses, getCustomer]) => {
                    if (!getCustomer) {
                        return new MatTableDataSource<TableData>([]);
                    }
                    return new MatTableDataSource(
                        contracts.map<TableData>(contract => ({
                            Abrechnungszyklus: contract.Abrechnungszyklus || '--',

                            ContractGroupTypeName: (
                                contractGroupTypes.find(
                                    cgt => cgt.Id === contract.ContractGroupTypeId,
                                ) || { Name: '--' }
                            ).Name,

                            ContractStatusName: (
                                contractStatuses.find(
                                    cs => cs.Id === contract.ContractStatusId,
                                ) || { Name: '--' }
                            ).Name,

                            Description: contract.Description || '--',
                            Name: contract.Name || '--',
                            Number: contract.Number || '--',
                            RouterLink: `${contract.Id}`,
                        })),
                    );
            }),
            // this.matSort sollte hier immer gesetzt sein (da ngAfterViewInit gefeuert haben muss),
            // aber das weiß TypeScript natürlich nicht.
            tap(dataSource => this.matSort && (dataSource.sort = this.matSort)),

            // Der Standard-`sortingDataAccessor` kommt mit numerischen Strings - wie der
            // Vertragsnummer - nicht klar und sortiert nicht. Workaround:
            tap(
                dataSource =>
                    (dataSource.sortingDataAccessor = (object, key) =>
                        `${object[key]}`.trim().toLowerCase()),
            ),

            // Vergleichsfunktion zum Freitext-Filtern
            tap(
                dataSource =>
                    (dataSource.filterPredicate = (data, searchTerm) =>
                        [
                            data.Abrechnungszyklus,
                            data.ContractGroupTypeName,
                            data.ContractStatusName,
                            data.Description,
                            data.Name,
                            data.Number,
                        ]
                            .map(value => value.trim().toLowerCase())
                            .some(value => value.includes(searchTerm))),
            ),

            // Sucheingaben an die DataSource weitergeben
            switchMap(dataSource =>
                this.InputForm.valueChanges.pipe(
                    startWith(''),
                    tap(searchTerm => (dataSource.filter = searchTerm.trim().toLowerCase())),
                    map(() => dataSource),
                ),
            ),
        );

        // Seiteneffekt: wenn das Suchfeld eingeblendet wird, fokussieren - asynchron, damit Angular
        // das Suchfeld erst anzeigt und danach fokussiert. Wenn ausgeblendet, den Suchwert löschen.
        this.IsSearching$.pipe(takeUntil(this.onDestroy$)).subscribe(isSearching =>
            isSearching
                ? setTimeout(() => this.inputRef && this.inputRef.nativeElement.focus())
                : this.InputForm.setValue(''),
        );
    }

    ngAfterViewInit() {
        this.afterViewInit$.next();
    }

    ngAfterViewChecked() {
        // bis zu einer Breite von ~800px können noch alle Header nebeneinander angezeigt werden
        const isSmallWidth = this.elementRef.nativeElement.clientWidth < 800;

        // nur aktualisieren, wenn sich der Wert geändert hat
        if (this.IsSmallWidth$.value !== isSmallWidth) {
            this.IsSmallWidth$.next(isSmallWidth);
        }
    }

    ngOnDestroy() {
        this.onDestroy$.next();
    }
}
